import type { Organization } from '@monorepo/shared'
import type { Tax } from '@monorepo/shared/schemas/taxes'
import {
  Building2,
  Calendar,
  Clock,
  CreditCard,
  Edit,
  Globe,
  Hash,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  User,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { TaxDialog } from '@/shared/components/taxes/TaxDialog'
import { TaxesSection } from '@/shared/components/taxes/TaxesSection'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toast } from '@/shared/lib/toast'
import {
  useAssignCustomerTaxes,
  useCustomerTaxes,
  useRemoveCustomerTax,
} from '../../hooks/useCustomerTaxes'
import {
  type CustomerResponse,
  customerService,
} from '../../services/customerService'
import { organizationsService } from '../../services/organizationsService'
import { OrganizationDrawer } from '../OrganizationDrawer'

interface CustomerDetailsTabProps {
  customer: CustomerResponse
  onCustomerUpdated?: (customer: CustomerResponse) => void
}

export function CustomerDetailsTab({
  customer,
  onCustomerUpdated,
}: CustomerDetailsTabProps) {
  const [isEditingCustomer, setIsEditingCustomer] = useState(false)
  const [isEditingBilling, setIsEditingBilling] = useState(false)
  const [isChangingOrganization, setIsChangingOrganization] = useState(false)
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false)
  const [isEditingOrgDetails, setIsEditingOrgDetails] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loadingOrg, setLoadingOrg] = useState(false)
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')

  // Tax state and hooks
  const [isTaxDialogOpen, setIsTaxDialogOpen] = useState(false)
  const { data: customerTaxesData, isLoading: _loadingTaxes } =
    useCustomerTaxes(customer.id)
  const assignTaxesMutation = useAssignCustomerTaxes()
  const removeTaxMutation = useRemoveCustomerTax()

  // Customer form state
  const [customerForm, setCustomerForm] = useState({
    name: customer.name,
    email: customer.email || '',
    external_id: customer.external_id || '',
    phone: customer.phone || '',
    status: customer.status,
    currency: customer.currency || 'USD',
    timezone: customer.timezone || 'UTC',
  })

  // Billing address form state
  const [billingForm, setBillingForm] = useState({
    street: customer.address_line1 || '',
    street2: customer.address_line2 || '',
    city: customer.city || '',
    state: customer.state || '',
    postal_code: customer.postal_code || '',
    country: customer.country || '',
  })

  // Fetch organization details if customer has organization_id
  useEffect(() => {
    if (customer.organization_id) {
      setLoadingOrg(true)
      organizationsService
        .getOrganization(customer.organization_id)
        .then(org => {
          setOrganization(org)
        })
        .catch(() => {})
        .finally(() => {
          setLoadingOrg(false)
        })
    }
  }, [customer.organization_id])

  // Sync customer form with customer data
  useEffect(() => {
    setCustomerForm({
      name: customer.name,
      email: customer.email || '',
      external_id: customer.external_id || '',
      phone: customer.phone || '',
      status: customer.status,
      currency: customer.currency || 'USD',
      timezone: customer.timezone || 'UTC',
    })
  }, [customer])

  // Sync billing form with customer data
  useEffect(() => {
    setBillingForm({
      street: customer.address_line1 || '',
      street2: customer.address_line2 || '',
      city: customer.city || '',
      state: customer.state || '',
      postal_code: customer.postal_code || '',
      country: customer.country || '',
    })
  }, [
    customer.address_line1,
    customer.address_line2,
    customer.city,
    customer.state,
    customer.postal_code,
    customer.country,
  ])

  // Load all organizations for selection
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await organizationsService.getOrganizations({
          page: 1,
          limit: 100,
        })
        setAllOrganizations(response.data)
      } catch {
        setAllOrganizations([])
      }
    }
    loadOrganizations()
  }, [])

  // Handle tax assignment
  const handleAddTaxRate = async (taxRate: Tax, _currency: string) => {
    assignTaxesMutation.mutate(
      {
        customerId: customer.id,
        data: {
          tax_ids: [taxRate.id],
          replace_existing: false,
        },
      },
      {
        onSuccess: () => {
          setIsTaxDialogOpen(false)
        },
      }
    )
  }

  // Handle tax removal
  const handleRemoveTax = (taxId: string) => {
    removeTaxMutation.mutate({
      customerId: customer.id,
      taxId,
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant='default'>Active</Badge>
      case 'inactive':
        return <Badge variant='secondary'>Inactive</Badge>
      case 'suspended':
        return <Badge variant='destructive'>Suspended</Badge>
      case 'archived':
        return <Badge variant='outline'>Archived</Badge>
      default:
        return <Badge variant='secondary'>Unknown</Badge>
    }
  }

  const _timezones = [
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Asia/Tokyo',
  ]
  const _now = new Date()
  const _formatInTimezone = (date: Date, timezone: string) => {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeStyle: 'short',
      dateStyle: 'short',
    }).format(date)
  }

  const updateCustomerForm = (updates: Partial<typeof customerForm>) => {
    setCustomerForm(prev => ({ ...prev, ...updates }))
  }

  const updateBillingForm = (updates: Partial<typeof billingForm>) => {
    setBillingForm(prev => ({ ...prev, ...updates }))
  }

  const handleAssignOrganization = async (organizationId: string) => {
    setIsSubmitting(true)
    try {
      const updated = await customerService.updateCustomer(customer.id, {
        organization_id: organizationId,
      })
      onCustomerUpdated?.(updated)
      toast.success('Organization assigned successfully')
    } catch {
      toast.error('Failed to assign organization')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOrganizationCreated = async (newOrganization: Organization) => {
    // Add to local list
    setAllOrganizations(prev => [...prev, newOrganization])
    // Assign to customer
    await handleAssignOrganization(newOrganization.id)
    // Close change dialog if open
    setIsChangingOrganization(false)
    setIsCreatingOrganization(false)
    setSelectedOrgId('')
  }

  const handleOrganizationUpdated = async (
    updatedOrganization: Organization
  ) => {
    // Refresh organization data
    setOrganization(updatedOrganization)
    // Update in local list
    setAllOrganizations(prev =>
      prev.map(org =>
        org.id === updatedOrganization.id ? updatedOrganization : org
      )
    )
    toast.success('Organization updated successfully')
  }

  const handleChangeOrganization = async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organization')
      return
    }
    await handleAssignOrganization(selectedOrgId)
    setIsChangingOrganization(false)
    setSelectedOrgId('')
  }

  const handleRemoveOrganization = async () => {
    setIsSubmitting(true)
    try {
      const updated = await customerService.updateCustomer(customer.id, {
        organization_id: null,
      })
      onCustomerUpdated?.(updated)
      setOrganization(null)
      toast.success('Organization removed successfully')
    } catch {
      toast.error('Failed to remove organization')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveCustomer = async () => {
    setIsSubmitting(true)
    try {
      const updated = await customerService.updateCustomer(customer.id, {
        name: customerForm.name,
        email: customerForm.email || undefined,
        external_id: customerForm.external_id || undefined,
        phone: customerForm.phone || undefined,
        status: customerForm.status,
        currency: customerForm.currency,
        timezone: customerForm.timezone,
      })
      onCustomerUpdated?.(updated)
      setIsEditingCustomer(false)
      toast.success('Customer details updated successfully')
    } catch {
      toast.error('Failed to update customer details')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveBilling = async () => {
    setIsSubmitting(true)
    try {
      const updated = await customerService.updateCustomer(customer.id, {
        address_line1: billingForm.street,
        address_line2: billingForm.street2 || undefined,
        city: billingForm.city,
        state: billingForm.state || undefined,
        postal_code: billingForm.postal_code,
        country: billingForm.country,
      })
      onCustomerUpdated?.(updated)
      setIsEditingBilling(false)
      toast.success('Billing address updated successfully')
    } catch {
      toast.error('Failed to update billing address')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isCustomerSaveDisabled = !customerForm.name.trim() || isSubmitting
  const isBillingSaveDisabled =
    !billingForm.street.trim() ||
    !billingForm.city.trim() ||
    !billingForm.postal_code.trim() ||
    !billingForm.country.trim() ||
    isSubmitting

  return (
    <div className='space-y-6'>
      {/* Customer Details */}
      <div className='border border-border rounded-lg p-6'>
        <div className='flex flex-row items-center justify-between mb-6'>
          <div>
            <h3 className='text-lg font-semibold'>Customer Details</h3>
            <p className='text-sm text-muted-foreground'>
              Basic information about the customer
            </p>
          </div>
          <Drawer
            open={isEditingCustomer}
            onOpenChange={setIsEditingCustomer}
            direction='right'
          >
            <DrawerTrigger asChild>
              <Button variant='outline' size='sm'>
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </Button>
            </DrawerTrigger>
            <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
              <div className='flex h-full flex-col'>
                <DrawerHeader className='px-6'>
                  <DrawerTitle>Edit Customer Details</DrawerTitle>
                  <DrawerDescription>
                    Update customer information and contact details
                  </DrawerDescription>
                </DrawerHeader>

                <div className='flex-1 overflow-y-auto px-6 pb-24 w-full'>
                  <div className='space-y-4 w-full max-w-none'>
                    <div className='space-y-2'>
                      <Label htmlFor='customer-name'>Name *</Label>
                      <Input
                        id='customer-name'
                        value={customerForm.name}
                        onChange={e =>
                          updateCustomerForm({ name: e.target.value })
                        }
                        placeholder='Customer Name'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='customer-email'>Email</Label>
                      <Input
                        id='customer-email'
                        type='email'
                        value={customerForm.email}
                        onChange={e =>
                          updateCustomerForm({ email: e.target.value })
                        }
                        placeholder='customer@example.com'
                      />
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='customer-external-id'>
                          External ID
                        </Label>
                        <Input
                          id='customer-external-id'
                          value={customerForm.external_id}
                          onChange={e =>
                            updateCustomerForm({ external_id: e.target.value })
                          }
                          placeholder='EXT-123'
                        />
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='customer-phone'>Phone</Label>
                        <Input
                          id='customer-phone'
                          value={customerForm.phone}
                          onChange={e =>
                            updateCustomerForm({ phone: e.target.value })
                          }
                          placeholder='+1-555-0123'
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='customer-currency'>Currency</Label>
                        <Select
                          value={customerForm.currency}
                          onValueChange={value =>
                            updateCustomerForm({ currency: value })
                          }
                        >
                          <SelectTrigger id='customer-currency'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='USD'>USD - US Dollar</SelectItem>
                            <SelectItem value='EUR'>EUR - Euro</SelectItem>
                            <SelectItem value='GBP'>
                              GBP - British Pound
                            </SelectItem>
                            <SelectItem value='JPY'>
                              JPY - Japanese Yen
                            </SelectItem>
                            <SelectItem value='CAD'>
                              CAD - Canadian Dollar
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='customer-timezone'>Timezone</Label>
                        <Select
                          value={customerForm.timezone}
                          onValueChange={value =>
                            updateCustomerForm({ timezone: value })
                          }
                        >
                          <SelectTrigger id='customer-timezone'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='UTC'>UTC</SelectItem>
                            <SelectItem value='America/New_York'>
                              America/New York
                            </SelectItem>
                            <SelectItem value='America/Los_Angeles'>
                              America/Los Angeles
                            </SelectItem>
                            <SelectItem value='Europe/London'>
                              Europe/London
                            </SelectItem>
                            <SelectItem value='Asia/Tokyo'>
                              Asia/Tokyo
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='customer-status'>Status</Label>
                        <Select
                          value={customerForm.status}
                          onValueChange={(
                            value:
                              | 'active'
                              | 'inactive'
                              | 'suspended'
                              | 'archived'
                          ) => updateCustomerForm({ status: value })}
                        >
                          <SelectTrigger id='customer-status'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='active'>Active</SelectItem>
                            <SelectItem value='inactive'>Inactive</SelectItem>
                            <SelectItem value='suspended'>Suspended</SelectItem>
                            <SelectItem value='archived'>Archived</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='sticky bottom-0 w-full border-t bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
                  <div className='flex items-center justify-between gap-3 px-6 py-4'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => setIsEditingCustomer(false)}
                    >
                      <X className='mr-2 h-4 w-4' /> Cancel
                    </Button>
                    <Button
                      type='button'
                      onClick={handleSaveCustomer}
                      disabled={isCustomerSaveDisabled}
                    >
                      {isSubmitting ? (
                        <span className='inline-flex items-center gap-2'>
                          <Loader2 className='h-4 w-4 animate-spin' />{' '}
                          Updating...
                        </span>
                      ) : (
                        <span className='inline-flex items-center gap-2'>
                          <Save className='h-4 w-4' /> Update Customer
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <User className='w-4 h-4' />
              Name
            </div>
            <div className='text-base font-medium'>{customer.name}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Mail className='w-4 h-4' />
              Email
            </div>
            <div className='text-base'>{customer.email || 'Not set'}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Phone className='w-4 h-4' />
              Phone
            </div>
            <div className='text-base'>{customer.phone || 'Not set'}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Hash className='w-4 h-4' />
              Customer ID
            </div>
            <div className='text-base font-mono text-xs'>{customer.id}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Hash className='w-4 h-4' />
              External ID
            </div>
            <div className='text-base font-mono'>
              {customer.external_id || 'Not set'}
            </div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1'>
              Status
            </div>
            <div>{getStatusBadge(customer.status)}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <CreditCard className='w-4 h-4' />
              Currency
            </div>
            <div className='text-base font-medium'>{customer.currency}</div>
          </div>

          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Clock className='w-4 h-4' />
              Timezone
            </div>
            <div className='text-base font-medium'>
              {customer.timezone || 'Not set'}
            </div>
          </div>
        </div>
      </div>

      {/* Organization */}
      <div className='border border-border rounded-lg p-6'>
        <div className='flex flex-row items-center justify-between mb-6'>
          <div>
            <h3 className='text-lg font-semibold'>Organization</h3>
            <p className='text-sm text-muted-foreground'>
              Organization details and contact information
            </p>
          </div>
          {(organization || customer.organization) && (
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setIsEditingOrgDetails(true)}
              >
                <Edit className='w-4 h-4 mr-2' />
                Edit Details
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setIsChangingOrganization(true)}
              >
                <RefreshCw className='w-4 h-4 mr-2' />
                Change
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleRemoveOrganization}
                disabled={isSubmitting}
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Remove
              </Button>
            </div>
          )}
          {!(organization || customer.organization) && (
            <div className='flex gap-2'>
              <Select
                value=''
                onValueChange={value => {
                  if (value !== 'create-new') {
                    handleAssignOrganization(value)
                  }
                }}
              >
                <SelectTrigger className='w-64'>
                  <SelectValue placeholder='Select organization' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='create-new'>
                    Create New Organization
                  </SelectItem>
                  {allOrganizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setIsCreatingOrganization(true)}
              >
                <Plus className='w-4 h-4 mr-2' />
                Create Organization
              </Button>
            </div>
          )}
        </div>

        {customer.organization_id || customer.organization ? (
          loadingOrg ? (
            <div className='text-center py-4'>
              <Loader2 className='w-6 h-6 animate-spin mx-auto text-muted-foreground' />
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                  <Building2 className='w-4 h-4' />
                  Organization Name
                </div>
                <div className='text-base font-medium'>
                  {organization?.name ||
                    customer.organization?.name ||
                    'Unknown'}
                </div>
              </div>

              {organization?.website && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                    <Globe className='w-4 h-4' />
                    Website
                  </div>
                  <a
                    href={organization.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-base text-blue-600 hover:underline'
                  >
                    {organization.website}
                  </a>
                </div>
              )}

              {organization?.email && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                    <Mail className='w-4 h-4' />
                    Contact Email
                  </div>
                  <div className='text-base'>{organization.email}</div>
                </div>
              )}

              {organization?.phone && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                    <Phone className='w-4 h-4' />
                    Phone
                  </div>
                  <div className='text-base'>{organization.phone}</div>
                </div>
              )}

              {organization?.industry && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1'>
                    Industry
                  </div>
                  <div className='text-base'>{organization.industry}</div>
                </div>
              )}

              {organization?.size && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1'>
                    Company Size
                  </div>
                  <div className='text-base capitalize'>
                    {organization.size}
                  </div>
                </div>
              )}

              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                  <CreditCard className='w-4 h-4' />
                  Currency
                </div>
                <div className='text-base font-medium'>
                  {organization?.currency || 'Not set'}
                </div>
              </div>

              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
                  <Clock className='w-4 h-4' />
                  Timezone
                </div>
                <div className='text-base font-medium'>
                  {organization?.timezone || 'Not set'}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className='text-center py-8 text-muted-foreground'>
            <Building2 className='w-12 h-12 mx-auto mb-3 text-muted-foreground/50' />
            <p>No organization assigned</p>
            <p className='text-sm'>
              Use the options above to assign or create an organization
            </p>
          </div>
        )}
      </div>

      {/* Change Organization Dialog */}
      <Dialog
        open={isChangingOrganization}
        onOpenChange={setIsChangingOrganization}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Organization</DialogTitle>
            <DialogDescription>
              Select a different organization to assign to this customer
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='org-select'>Organization</Label>
              <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                <SelectTrigger id='org-select'>
                  <SelectValue placeholder='Select an organization' />
                </SelectTrigger>
                <SelectContent>
                  {allOrganizations
                    .filter(org => org.id !== customer.organization_id)
                    .map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className='pt-2'>
              <Button
                variant='outline'
                className='w-full'
                onClick={() => {
                  setIsCreatingOrganization(true)
                  setIsChangingOrganization(false)
                }}
              >
                <Plus className='w-4 h-4 mr-2' />
                Create New Organization
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsChangingOrganization(false)
                setSelectedOrgId('')
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeOrganization}
              disabled={!selectedOrgId || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Changing...
                </>
              ) : (
                <>
                  <RefreshCw className='w-4 h-4 mr-2' />
                  Change Organization
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Address */}
      <div className='border border-border rounded-lg p-6'>
        <div className='flex flex-row items-center justify-between mb-6'>
          <div>
            <h3 className='text-lg font-semibold'>Billing Address</h3>
            <p className='text-sm text-muted-foreground'>
              Billing address information
            </p>
          </div>
          <Drawer
            open={isEditingBilling}
            onOpenChange={setIsEditingBilling}
            direction='right'
          >
            <DrawerTrigger asChild>
              <Button variant='outline' size='sm'>
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </Button>
            </DrawerTrigger>
            <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
              <div className='flex h-full flex-col'>
                <DrawerHeader className='px-6'>
                  <DrawerTitle>Edit Billing Address</DrawerTitle>
                  <DrawerDescription>
                    Update billing address information
                  </DrawerDescription>
                </DrawerHeader>

                <div className='flex-1 overflow-y-auto px-6 pb-24 w-full'>
                  <div className='space-y-4 w-full max-w-none'>
                    <div className='space-y-2'>
                      <Label htmlFor='billing-street'>Address Line 1 *</Label>
                      <Input
                        id='billing-street'
                        value={billingForm.street}
                        onChange={e =>
                          updateBillingForm({ street: e.target.value })
                        }
                        placeholder='123 Main Street'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='billing-street2'>Address Line 2</Label>
                      <Input
                        id='billing-street2'
                        value={billingForm.street2}
                        onChange={e =>
                          updateBillingForm({ street2: e.target.value })
                        }
                        placeholder='Suite 100'
                      />
                    </div>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='billing-city'>City *</Label>
                        <Input
                          id='billing-city'
                          value={billingForm.city}
                          onChange={e =>
                            updateBillingForm({ city: e.target.value })
                          }
                          placeholder='New York'
                        />
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='billing-state'>State</Label>
                        <Input
                          id='billing-state'
                          value={billingForm.state}
                          onChange={e =>
                            updateBillingForm({ state: e.target.value })
                          }
                          placeholder='NY'
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='billing-country'>Country *</Label>
                        <Input
                          id='billing-country'
                          value={billingForm.country}
                          onChange={e =>
                            updateBillingForm({ country: e.target.value })
                          }
                          placeholder='United States'
                        />
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='billing-postal'>Post Code *</Label>
                        <Input
                          id='billing-postal'
                          value={billingForm.postal_code}
                          onChange={e =>
                            updateBillingForm({ postal_code: e.target.value })
                          }
                          placeholder='10001'
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='sticky bottom-0 w-full border-t bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
                  <div className='flex items-center justify-between gap-3 px-6 py-4'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => setIsEditingBilling(false)}
                    >
                      <X className='mr-2 h-4 w-4' /> Cancel
                    </Button>
                    <Button
                      type='button'
                      onClick={handleSaveBilling}
                      disabled={isBillingSaveDisabled}
                    >
                      {isSubmitting ? (
                        <span className='inline-flex items-center gap-2'>
                          <Loader2 className='h-4 w-4 animate-spin' />{' '}
                          Updating...
                        </span>
                      ) : (
                        <span className='inline-flex items-center gap-2'>
                          <Save className='h-4 w-4' /> Update Address
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        {customer.address_line1 ? (
          <div className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Address Line 1
                </div>
                <div className='text-base'>{customer.address_line1}</div>
              </div>
              {customer.address_line2 && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1'>
                    Address Line 2
                  </div>
                  <div className='text-base'>{customer.address_line2}</div>
                </div>
              )}
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  City
                </div>
                <div className='text-base'>{customer.city}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  State
                </div>
                <div className='text-base'>{customer.state || 'N/A'}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Post Code
                </div>
                <div className='text-base'>{customer.postal_code}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Country
                </div>
                <div className='text-base'>{customer.country}</div>
              </div>
            </div>
          </div>
        ) : organization?.address_line1 ? (
          <div className='space-y-4'>
            <div className='mb-4 p-3 bg-muted rounded-md text-sm text-muted-foreground'>
              <p>
                Using organization address. Click Edit to set a
                customer-specific address.
              </p>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Address Line 1
                </div>
                <div className='text-base'>{organization.address_line1}</div>
              </div>
              {organization.address_line2 && (
                <div>
                  <div className='text-sm font-medium text-muted-foreground mb-1'>
                    Address Line 2
                  </div>
                  <div className='text-base'>{organization.address_line2}</div>
                </div>
              )}
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  City
                </div>
                <div className='text-base'>{organization.city}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  State
                </div>
                <div className='text-base'>{organization.state || 'N/A'}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Post Code
                </div>
                <div className='text-base'>{organization.postal_code}</div>
              </div>
              <div>
                <div className='text-sm font-medium text-muted-foreground mb-1'>
                  Country
                </div>
                <div className='text-base'>{organization.country}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className='text-center py-8 text-muted-foreground'>
            <MapPin className='w-12 h-12 mx-auto mb-3 text-muted-foreground/50' />
            <p>No billing address set</p>
          </div>
        )}
      </div>

      {/* Taxes Section */}
      <TaxesSection
        taxes={customerTaxesData?.effective_taxes || []}
        onAddTax={() => setIsTaxDialogOpen(true)}
        onRemoveTax={handleRemoveTax}
        showActions={true}
        taxExempt={customerTaxesData?.tax_exempt || false}
        taxExemptionCertificate={customerTaxesData?.tax_exemption_certificate}
        taxExemptionReason={customerTaxesData?.tax_exemption_reason}
      />

      {/* Account Information */}
      <div className='border border-border rounded-lg p-6'>
        <div className='mb-6'>
          <h3 className='text-lg font-semibold'>Account Information</h3>
          <p className='text-sm text-muted-foreground'>
            Account creation and activity details
          </p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Calendar className='w-4 h-4' />
              Customer Since
            </div>
            <div className='text-base font-medium'>
              {formatDate(customer.created_at)}
            </div>
          </div>
          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2'>
              <Clock className='w-4 h-4' />
              Last Updated
            </div>
            <div className='text-base font-medium'>
              {formatDate(customer.updated_at)}
            </div>
          </div>
          <div>
            <div className='text-sm font-medium text-muted-foreground mb-1'>
              Active Contracts
            </div>
            <div className='text-base font-medium'>
              {customer.stats?.active_contracts}
            </div>
          </div>
        </div>
      </div>

      {/* Tax Dialog */}
      <TaxDialog
        open={isTaxDialogOpen}
        onOpenChange={setIsTaxDialogOpen}
        existingTaxes={customerTaxesData?.customer_taxes || []}
        onAddTaxRate={handleAddTaxRate}
        title='Add Tax to Customer'
        description='Select a tax rate from the catalog to apply to this customer'
        submitButtonText='Add Tax'
      />

      {/* Organization Edit Drawer */}
      {organization && (
        <OrganizationDrawer
          organization={organization}
          open={isEditingOrgDetails}
          onOpenChange={setIsEditingOrgDetails}
          onOrganizationCreated={handleOrganizationUpdated}
        />
      )}

      {/* Organization Create Drawer */}
      <OrganizationDrawer
        open={isCreatingOrganization}
        onOpenChange={open => {
          setIsCreatingOrganization(open)
          if (!open && isChangingOrganization) {
            // Re-open change dialog if we were creating from there
            setIsChangingOrganization(true)
          }
        }}
        onOrganizationCreated={handleOrganizationCreated}
      />
    </div>
  )
}
